import React, { FC, useCallback, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { NotificationContext } from '../../contexts/NotificationContext';
import { EditCurrentOrganizationForm } from './EditCurrentOrganizationForm';

export type TEditCurrentOrganizationFormType = {
  address: string;
  billing_address: string;
  phone_number: string;
  country?: string;
  same_billing_address: boolean;
};
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { useTranslation } from 'react-i18next';
import { compareChanges } from '../../helpers/compareChanges';
import { apiOrganization } from '../../resources/api/apiOrganization';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { CurrentUserContext } from '../../contexts/CurrentUserContext';
import { sanitizeOrganizationValidation } from '../../helpers/sanitizeOrganizationValidation';

interface IProps {
  open: boolean;
  closeDialog: () => void;
}

const { useUpdateCurrentOrganizationByName } = apiOrganization;

export const EditCurrentOrganizationDialog: FC<IProps> = ({ open, closeDialog }) => {
  const { organization, setOrganization } = useContext(OrganizationContext);
  const { refetchUserInfo } = useContext(CurrentUserContext);
  const { execute: updateOrganization } = useUpdateCurrentOrganizationByName(organization?.name ?? '');
  const { addNotification } = useContext(NotificationContext);
  const handleError = useApiErrorHandler();
  const { t } = useTranslation();

  const editOrganizationForm = useForm<TEditCurrentOrganizationFormType>();
  useEffect(() => {
    if (!open) {
      editOrganizationForm.reset();
    } else if (organization) {
      editOrganizationForm.setValue('same_billing_address', organization.address === organization.billing_address);
    }
  }, [open, organization]);

  const onSubmit = useCallback(
    async (fields: TEditCurrentOrganizationFormType) => {
      if (!organization) return;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { same_billing_address, ...fieldsToCompare } = fields;
      // same_billing_address is handled by the form component's useEffect
      const { anythingChanged: anythingToUpdate, changes: payload } = compareChanges(
        {
          address: organization.address,
          phone_number: organization.phone_number ?? '',
          billing_address: organization.billing_address,
          country: (organization as any).country ?? '',
        },
        fieldsToCompare
      );

      if (anythingToUpdate) {
        try {
          const updatedData = await updateOrganization({
            ...payload,
            phone_number: fieldsToCompare.phone_number || undefined,
            country: fieldsToCompare.country || undefined,
          });

          // Update the organization context with the new data
          setOrganization(updatedData.payload);

          // Refetch user info to get updated organization data
          await refetchUserInfo();

          closeDialog();

          addNotification({
            severity: 'success',
            message: t('editOrganizationDialog.edit.success'),
          });
        } catch (e) {
          sanitizeOrganizationValidation(e);
          handleError(e);
        }
      } else {
        addNotification({
          severity: 'warning',
          message: t('editOrganizationDialog.edit.nothingChanged'),
        });
      }
    },
    [organization, updateOrganization, closeDialog, setOrganization, addNotification, t, handleError, refetchUserInfo]
  );

  if (!organization) return null;

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>{t('editOrganizationDialog.edit.title')}</DialogTitle>
      <DialogContent>
        <EditCurrentOrganizationForm onSubmit={onSubmit} organization={organization} form={editOrganizationForm} />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('common.dialog.close')}</Button>
        <Button variant="contained" color="primary" onClick={editOrganizationForm.handleSubmit(onSubmit)}>
          {t('editOrganizationDialog.edit.confirmButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
